import React, { useState, useEffect } from "react";
import SessionListNewView from "./sessionListNew.view";
import * as SessionService from "../../../../services/SessionService";
import * as FarmService from "../../../../services/visual/FarmService";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShowAlertDialog } from "@de-prado-dev/react-component-library";
import PATHS from "../../../../constants/paths";
import moment from "moment";
import "moment/locale/es";
import * as CourseService from "../../../../services/CourseService";
import * as XLSX from "xlsx";

const SessionListNewContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  const itemsPerPage = 12; // 12 cards por página (4x3 grid)
  const [courses, setCourses] = useState([]);

  const location = useLocation();
  const filterMeeting = location.state?.filters;

  const loadSessions = async (
    page = 1,
    appliedFilters = {},
    completed = false,
  ) => {
    try {
      const filter = {
        filters: {
          ...appliedFilters,
          ...filterMeeting,
          ...(completed ? { completed: true } : {}),
        },
        sort: "date",
        page: page,
      };
      const result = await SessionService.listFilter(filter);
      if (result && result.docs) {
        setSessions(result.docs);
        setTotalPages(result.totalPages || 1);
        setTotalDocs(result.totalDocs || 0);
        setCurrentPage(page);
      }
      const courses = await CourseService.listCourse({});
      setCourses(courses);
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  useEffect(() => {
    loadSessions(currentPage, filters, showCompleted);
    setForceRefresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, forceRefresh, showCompleted]);

  const handleFilterSubmit = (values) => {
    let newFilter = {};
    Object.entries(values).forEach(([key, val]) => {
      // Ignorar valores vacíos, undefined o null
      if (val === undefined || val === null || val === "") {
        return;
      }
      // Si es un objeto (del autocomplete con { label, value }), extraer el valor
      if (typeof val === "object" && val.value !== undefined) {
        newFilter[key] = val.value;
      }
      // Si es un string, verificar que no esté vacío
      else if (typeof val === "string" && val.length > 0) {
        newFilter[key] = val;
      }
      // Para otros tipos (números, booleanos, fechas), incluirlos
      else if (typeof val !== "string") {
        newFilter[key] = val;
      }
    });
    setFilters(newFilter);
    setCurrentPage(1);
  };

  const handleCleanFilter = (resetForm) => {
    resetForm();
    setFilters({});
    setCurrentPage(1);
  };

  const handleFilterFinca = async (value) => {
    const filter = {
      filters: {
        name: value,
      },
    };
    const result = await FarmService.listFarms(filter.filters);
    return result;
  };

  const handleNewSession = () => {
    navigate(PATHS.CREATE_SESSION);
  };

  const handleViewSession = (session) => {
    navigate(`/adm/session/attendance/${session.uuid}`, {
      state: session,
    });
  };

  const handleEditSession = (uuid) => {
    navigate(`/adm/session/form/update/${uuid}`);
  };

  const handleConfirmSession = async (uuid) => {
    const title = t("forms.session.dialog.title");
    const text = t("forms.session.dialog.text");

    const okAction = async () => {
      try {
        const data = { completed: true };
        const result = await SessionService.completeSession(uuid, data);

        if (result) {
          ShowAlertDialog(
            t("forms.session.messages.close.title"),
            t("forms.session.messages.close.success"),
            "success",
            false,
            "OK",
            false,
            () => setForceRefresh(true),
          );
        } else {
          ShowAlertDialog(
            "Error",
            t("forms.session.messages.close.error"),
            "error",
            false,
            "OK",
            false,
          );
        }
      } catch (error) {
        console.error("Error confirming session:", error);
      }
    };

    ShowAlertDialog(
      title,
      text,
      "warning",
      true,
      t("forms.session.dialog.buttonOk"),
      false,
      okAction,
      t("forms.session.dialog.buttonNo"),
      () => {},
    );
  };

  const handleDeleteSession = async (uuid) => {
    const okAction = async () => {
      try {
        const result = await SessionService.deleteSession(uuid);
        if (result === "OK") {
          setForceRefresh(true);
        }
      } catch (error) {
        console.error("Error deleting session:", error);
      }
    };

    ShowAlertDialog(
      t("forms.form.dialog.remove.title"),
      t("forms.form.dialog.remove.text"),
      "warning",
      true,
      t("forms.form.dialog.remove.buttonOk"),
      false,
      okAction,
      t("forms.form.dialog.remove.buttonNo"),
      () => {},
    );
  };

  const handleDownloadDocument = async (uuid) => {
    if (uuid !== null) {
      try {
        const result = await SessionService.generateDocument(uuid);
        const session = await SessionService.getSession(uuid);
        const year = moment(session.date).year();
        const month = moment(session.date).month() + 1;
        const day = moment(session.date).date();
        const nameCourse = session.session.meeting?.course?.name.replace(
          /\s+/g,
          "",
        );
        const url = window.URL.createObjectURL(result);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${year}_${month}_${day}_${nameCourse}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        if (result) {
          ShowAlertDialog(
            t("forms.attendance.messages.download.title"),
            t("forms.attendance.messages.download.success"),
            "success",
            false,
            "OK",
            false,
          );
        } else {
          ShowAlertDialog(
            "Error",
            t("forms.attendance.messages.download.error"),
            "error",
            false,
            "OK",
            false,
          );
        }
      } catch (error) {
        console.error("Error downloading document:", error);
        ShowAlertDialog(
          "Error",
          t("forms.attendance.messages.download.error"),
          "error",
          false,
          "OK",
          false,
        );
      }
    }
  };

  const handlePageChange = (page) => {
    loadSessions(page, filters, showCompleted);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const exportToExcel = async () => {
    const filter = {
      filters: filters,
      sort: "date",
    };
    SessionService.listToExport(filter).then((result) => {
      const uniqueCourses = new Set(
        result.data.map((item) => item.course?.uuid),
      );

      const coursesDif = uniqueCourses.size;
      const employeeData = result.dataEmployee;
      const data = result.data.map((item) => {
        let fila;

        let asistentesFirmados = item.completed ? item.employees.length : 0;
        let asistentesNoFirmados = item.completed
          ? item.employeesNotSigned.length
          : item.employees.length;
        let asistentesTotales = asistentesFirmados + asistentesNoFirmados;
        let percentageSigned = item.completed
          ? ((asistentesFirmados / asistentesTotales) * 100).toFixed(2) + "%"
          : "0%";
        let percentageNotSigned = item.completed
          ? ((asistentesNoFirmados / asistentesTotales) * 100).toFixed(2) + "%"
          : "0%";

        fila = {
          A: moment(item.date).format("DD/MM/YYYY"),
          B: moment(item.date).format("HH:mm"),
          C: item.user?.name || "N/A",
          D: item.course?.name || "N/A",
          E: item.meeting?.center?.name || "N/A",
          F:
            item.meeting?.center?.country === 205
              ? "ES"
              : item.meeting?.center?.country === 176
                ? "PT"
                : "",
          G: item.completed ? "Completada" : "Pendiente",
          H: asistentesTotales,
          I: asistentesFirmados,
          J: asistentesNoFirmados,
          K: percentageSigned,
          // K: percentageNotSigned,
        };
        return fila;
      });

      const headers = {
        A: "Fecha",
        B: "Hora",
        C: "Formador",
        D: "Contenido",
        E: "Centro",
        F: "País",
        G: "Estado",
        H: "Convocados",
        I: "Asistentes",
        J: "No asistidos",
        K: "% No firmados",
      };

      if (employeeData) {
        const summaryRow1 = {
          A: "DNI empleado",
          B: "Apellidos, Nombre",
          C: "Cursos asistidos",
          D: "",
          E: "",
          F: "",
          G: "",
          H: "",
          I: "",
          J: "",
          K: "",
        };
        const summaryRow2 = {
          A: employeeData.nif,
          B: employeeData.name,
          C: coursesDif,
          D: "",
          E: "",
          F: "",
          G: "",
          H: "",
          I: "",
          J: "",
          K: "",
        };
        const summaryRow3 = {
          A: "",
          B: "",
          C: "",
          D: "",
          E: "",
          F: "",
          G: "",
          H: "",
          I: "",
          J: "",
          K: "",
        };
        data.unshift(summaryRow3);
        data.unshift(summaryRow2);
        data.unshift(summaryRow1);

        data.splice(3, 0, headers);
      } else {
        data.unshift(headers);
      }

      const filename = `Sesiones${moment(new Date()).format("DDMMYYYYHHmm")}`;
      const sheetName = "Listado de Sesiones";
      var ws = XLSX.utils.json_to_sheet(data, {
        header: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"],
        skipHeader: true,
        UTC: true,
        dateNF: "dd-mm-yyyy hh:mm;@",
      });

      // dateNF:'mm/dd/yyyy;@',cellDates:true, raw: true}
      const wb = XLSX.utils.book_new();
      wb.Props = {
        Title: "Listado de sesiones",
        Subject: "Exportación de sesiones",
        Author: "Assista",
        CreatedDate: new Date(),
      };
      wb.SheetNames.push(sheetName);
      wb.Sheets[sheetName] = ws;
      XLSX.writeFile(wb, filename + ".xlsx", {
        bookType: "xlsx",
        type: "binary",
      });
    });
  };

  return (
    <SessionListNewView
      sessions={sessions}
      filters={filters}
      showCompleted={showCompleted}
      onShowCompletedChange={setShowCompleted}
      onFilterSubmit={handleFilterSubmit}
      onCleanFilter={handleCleanFilter}
      onFilterFinca={handleFilterFinca}
      onNewSession={handleNewSession}
      onViewSession={handleViewSession}
      onEditSession={handleEditSession}
      onConfirmSession={handleConfirmSession}
      onDeleteSession={handleDeleteSession}
      onDownloadDocument={handleDownloadDocument}
      currentPage={currentPage}
      totalPages={totalPages}
      totalDocs={totalDocs}
      onPageChange={handlePageChange}
      courses={courses}
      onExportToExcel={exportToExcel}
    />
  );
};

SessionListNewContainer.propTypes = {};

export default SessionListNewContainer;
